import type { Opportunity, OpportunityFilter } from '@carboncopyco/types';
import { LoadingDots, MenuItem } from '@carboncopyco/ui';
import { capitalize } from '@carboncopyco/utils';
import { Combobox } from '@headlessui/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { type MutableRefObject, forwardRef } from 'react';
import { OpportunityDropdownEmptyState } from './OpportunityDropdownEmptyState';

type OpportunityListProps = {
  query: string;
  opportunities: Opportunity[];
  isIntegrationConnected: boolean;
  isLoading: boolean;
  selectedFilter: OpportunityFilter;
  setFilter: (filter: OpportunityFilter) => void;
};

export const OpportunityList = forwardRef<HTMLElement, OpportunityListProps>(
  (
    {
      query,
      opportunities,
      isIntegrationConnected,
      isLoading,
      selectedFilter,
      setFilter,
    },
    ref
  ) => {
    const rowVirtualizer = useVirtualizer({
      count: opportunities.length,
      getScrollElement: () =>
        (ref as MutableRefObject<HTMLDivElement>)?.current,
      estimateSize: () => 40,
      overscan: 20,
    });

    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-[130px]">
          <LoadingDots />
        </div>
      );
    }

    if (!opportunities.length) {
      return (
        <div className="flex justify-center items-center h-[130px]">
          <OpportunityDropdownEmptyState
            disconnected={!isIntegrationConnected}
            filter={selectedFilter}
            query={query}
            handleToggleFilter={setFilter}
          />
        </div>
      );
    }

    return (
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const opportunity = opportunities[virtualRow.index];
          return (
            <Combobox.Option
              key={virtualRow.index}
              value={opportunity}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              {({ active, disabled }) => (
                <MenuItem
                  focus={active}
                  disabled={disabled}
                  item={{
                    id: opportunity.id as string,
                    label: capitalize(opportunity.name),
                  }}
                />
              )}
            </Combobox.Option>
          );
        })}
      </div>
    );
  }
);
OpportunityList.displayName = 'OpportunityList';
